/**
 * IronbodySkillEffect — Grit ironbody-path toggle stance.
 *
 * A kinetic shell that trades mobility for resilience: 25% incoming DR,
 * doubled knockback resistance, 0.2s stagger on every melee hit the
 * player lands. Movement speed drops 30%.
 *
 * Toggle on → apply `ironbody_active` buff. Toggle off → remove buff,
 * start cooldown. While active, external systems call
 * `handlePlayerMeleeHit()` to fire stagger on struck enemies.
 *
 * Supports upgrade keys:
 * - Hardening: `ironbodyDamageReduction` (additive, stacks with base 0.25)
 * - Grounded: `knockbackRecoilDamage` (additive, recoil on knockback attempt)
 * - Defiance: `defianceChargeRate` via ChargeSystem (charge on damage taken)
 *
 * Extends ToggleSkillEffect for active/cooldown lifecycle.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Ironbody
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface IronbodyDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    remove(statusId: string, ...args: unknown[]): void;
    has(statusId: string, ...args: unknown[]): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface IronbodyConfig {
  readonly cooldownMs: number;
  readonly baseDamageReduction: number;
  readonly baseKnockbackResistanceMultiplier: number;
  readonly movementSpeedMultiplier: number;
  readonly staggerDurationMs: number;
}

const DEFAULT_CONFIG: IronbodyConfig = {
  cooldownMs: 800,                       // medium cooldown tier
  baseDamageReduction: 0.25,             // 25% incoming DR
  baseKnockbackResistanceMultiplier: 2,  // doubled knockback resistance
  movementSpeedMultiplier: 0.7,          // 30% movement speed penalty
  staggerDurationMs: 200,               // 0.2s stagger on melee hits
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class IronbodySkillEffect extends ToggleSkillEffect {
  readonly skillId: string = 'ironbody';

  private readonly deps: IronbodyDeps;
  private readonly config: IronbodyConfig;

  // Resolved values
  private _resolvedDr = 0;
  private _buffApplied = false;
  private _staggersApplied = 0;
  private _recoilDamageDealt = 0;

  constructor(deps: IronbodyDeps, config: Partial<IronbodyConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get resolvedDr(): number { return this._resolvedDr; }
  get buffApplied(): boolean { return this._buffApplied; }
  get staggersApplied(): number { return this._staggersApplied; }
  get recoilDamageDealt(): number { return this._recoilDamageDealt; }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._isActive = true;
    this._staggersApplied = 0;
    this._recoilDamageDealt = 0;

    // Resolve DR (base + Hardening bonus)
    const bonusDr = this.deps.resolver.getAdditiveEffect('ironbody', 'damageReduction');
    this._resolvedDr = this.config.baseDamageReduction + bonusDr;

    // Apply buff with resolved DR as intensity so the damage pipeline reads it.
    // Knockback resistance and movement penalty are read from the skill config
    // directly, not from the tracker.
    this.deps.statusEffects.apply(
      'ironbody_active',
      -1, // permanent until deactivated
      this._resolvedDr,
      'ironbody',
    );
    this._buffApplied = true;

    return true;
  }

  protected onDeactivate(): void {
    this.removeBuff();
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(_delta: number): void {
    // Active logic is event-driven via handlePlayerMeleeHit() and
    // handleKnockbackAttempt(), not polled per-frame
  }

  destroy(): void {
    this.removeBuff();
    super.destroy();
  }

  // --- External event handlers ---

  /**
   * Called by the game layer when the player lands a melee hit while
   * Ironbody is active. Applies stagger to the struck enemy.
   */
  handlePlayerMeleeHit(): void {
    if (!this._isActive) return;

    this.deps.statusEffects.apply('stagger', this.config.staggerDurationMs, 'ironbody');
    this._staggersApplied++;
  }

  /**
   * Called by the game layer when an enemy attempts to knock back the
   * player while Ironbody is active. If Grounded is unlocked, applies
   * recoil damage to the attacker.
   *
   * @returns The recoil damage dealt, or 0 if Grounded is not unlocked.
   */
  handleKnockbackAttempt(): number {
    if (!this._isActive) return 0;

    if (this.deps.resolver.hasEffect('ironbody', 'knockbackRecoilDamage')) {
      const recoil = this.deps.resolver.getAdditiveEffect('ironbody', 'knockbackRecoilDamage');
      this._recoilDamageDealt += recoil;
      return recoil;
    }

    return 0;
  }

  // --- Internal ---

  private removeBuff(): void {
    if (this._buffApplied) {
      this.deps.statusEffects.remove('ironbody_active', 'ironbody');
      this._buffApplied = false;
    }
  }
}
